import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import type { EmailCampaign } from 'types/campaign'
import type { State } from 'types/redux'
import { loadCampaignStats } from '@disco/state-management/actions/campaign-stats-actions'
import { getCampaign } from '@disco/state-management/selectors/campaigns'
import UserList from 'components/common/dashboard/card-content/users-list'
import Button from 'components/ui/button'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import type { RootState } from 'store/configureStore'

interface OwnProps {
  navigate: NavigateFunction
  params: {
    campaignId: string
  }
}
type OwnState = {
  showInactiveUsers: boolean
}

function mapStateToProps(state: RootState, ownProps: OwnProps) {
  return {
    campaign: getCampaign(state, {
      campaignId: parseInt(ownProps.params.campaignId),
    }),
  }
}

const mapDispatchToProps = {
  loadCampaignStats,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
const EMPTY_ARRAY = []
type Props = PropsFromRedux & OwnProps

class CampaignSummary extends React.Component<Props, OwnState> {
  state = {
    showInactiveUsers: false,
  }

  componentDidMount() {
    this.props.loadCampaignStats(
      parseInt(this.props.params.campaignId, 10),
      true,
    )
  }

  _onUsersSwitch = () =>
    this.setState({
      showInactiveUsers: !this.state.showInactiveUsers,
    })
  _onClose = () => {
    const {
      params: { campaignId },
    } = this.props
    this.props.navigate(`/campaigns/${campaignId}/stats/dashboard`)
  }

  render() {
    const {
      campaign,
      params: { campaignId },
    } = this.props
    if (!campaign) return null
    const allUsers = campaign.stats
      ? campaign.stats.most_engaged_users || []
      : EMPTY_ARRAY
    const activeUsers = allUsers.filter(
      u =>
        u.playlist_downloads > 0 ||
        u.track_downloads > 0 ||
        u.email_opens > 0 ||
        u.page_opens > 0 ||
        u.streams > 0,
    )
    const showInactiveSwitch = activeUsers.length < allUsers.length
    const switchText = this.state.showInactiveUsers
      ? 'Hide inactive users'
      : 'Show inactive users'
    return (
      <ModalWindow
        header={<Heading>Most engaged users</Heading>}
        footer={
          campaign.stats && !campaign.stats.loading && showInactiveSwitch ? (
            <Button caption={switchText} onClick={this._onUsersSwitch} />
          ) : null
        }
        isOpen={true}
        onClose={this._onClose}
      >
        <UserList
          users={this.state.showInactiveUsers ? allUsers : activeUsers}
        />
      </ModalWindow>
    )
  }
}

const ConnectedCampaignSummary = connector(CampaignSummary)

function CampaignSummaryWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ campaignId: string }>()

  const props: OwnProps = {
    navigate,
    params: params as { campaignId: string },
  }

  return <ConnectedCampaignSummary {...props} />
}

export default CampaignSummaryWithRouter
